/**
 * @file 本だな (取り込んだ資料の一覧) を取得する hook。
 *
 * @what: familyId ごとに読み、画面をまたいで覚えを使い回す (lib/sharedFetchCache.ts)。期限内の覚えが
 *   あれば通信せずに ready で始まり、期限を過ぎていれば覚えを出したまま取り直す。
 *   空配列 = まだ何も取り込んでいない。
 * @why: 資料は家族の持ち物で学習者に従属しないため、plan の draft 機構とは独立に持つ。一覧は目次込みで
 *   約360KB あり、本だな・リーダー・さくいん・解説の面が mount のたびに取り直すと、本を開くたびに
 *   同じものを運ぶことになる。資料は CLI でしか変わらないので、期限を長めに置く。
 * @failure: 失敗は `error` (画面は1行で知らせる。詳細は console のみ)。期限切れの覚えがあるときは
 *   それを出したままにする (古い一覧でも本だなは出せる)。
 */

import { useEffect, useState } from "react";
import { fetchStudySources, studySourcesCacheKey, type StudySource } from "../api/sources";
import { loadShared, readShared } from "../lib/sharedFetchCache";

export type StudySourcesState =
  | { status: "loading" }
  | { status: "ready"; sources: StudySource[] }
  | { status: "error" };

/** 覚えを取り直さずに使う期間である。 */
export const STUDY_SOURCES_TTL_MS = 10 * 60 * 1000;

function fromCache(key: string): StudySourcesState {
  const hit = readShared<StudySource[]>(key, STUDY_SOURCES_TTL_MS);
  return hit ? { status: "ready", sources: hit.value } : { status: "loading" };
}

export function useStudySources(familyId: string): StudySourcesState {
  const key = studySourcesCacheKey(familyId);
  const [state, setState] = useState<StudySourcesState>(() => fromCache(key));

  useEffect(() => {
    let cancelled = false;
    const hit = readShared<StudySource[]>(key, STUDY_SOURCES_TTL_MS);
    setState((prev) => {
      if (hit) {
        return prev.status === "ready" && prev.sources === hit.value
          ? prev
          : { status: "ready", sources: hit.value };
      }
      return prev.status === "loading" ? prev : { status: "loading" };
    });
    if (hit?.fresh) {
      return () => {
        cancelled = true;
      };
    }
    loadShared(key, () => fetchStudySources(familyId))
      .then((sources) => {
        if (!cancelled) setState({ status: "ready", sources });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("[study] failed to load study sources", err);
        if (!hit) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [familyId, key]);

  return state;
}
